import type { ButtonHTMLAttributes, ReactElement, ReactNode } from 'react';
import { Slot } from '@radix-ui/react-slot';

import { classNames } from '@m1work/shared-utils';

import styles from './IconButton.module.scss';

export type IconButtonVariant = 'fill' | 'outlined' | 'ghost' | 'overlay';
export type IconButtonColor = 'primary' | 'accent' | 'error' | 'soft';
export type IconButtonSize = 'sm' | 'md' | 'lg';

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    /** Round shape. @default true */
    rounded?: boolean;
    /** Render the child element (e.g. the app's `<Link>`) with these styles. @default false */
    asChild?: boolean;
    /** `overlay` is a dark translucent button over photos and video. @default 'ghost' */
    variant?: IconButtonVariant;
    /** Colour role. @default 'primary' */
    color?: IconButtonColor;
    /** @default 'md' */
    size?: IconButtonSize;
    /** Pressed / active look. @default false */
    selected?: boolean;
    /** Required: the button has no visible text (translated). */
    'aria-label': string;
    /** The icon. */
    children: ReactNode;
}

/**
 * A button with only an icon. Its accessible name comes from `aria-label`, which is required.
 *
 * @example
 * <IconButton aria-label={t('common.close')} onClick={onClose}>
 *     <XIcon />
 * </IconButton>
 */
export function IconButton({
    rounded = true,
    asChild = false,
    variant = 'ghost',
    color = 'primary',
    size = 'md',
    selected = false,
    className,
    ...props
}: IconButtonProps): ReactElement {
    const Comp = asChild ? Slot : 'button';

    return (
        <Comp
            className={classNames(
                styles['icon-button'],
                styles[`variant-${variant}`],
                styles[`color-${color}`],
                styles[`size-${size}`],
                rounded && styles.rounded,
                selected && styles.selected,
                className,
            )}
            {...props}
        />
    );
}
